/**
 * API Client - Frontend API wrapper for StreamCast
 */

// Build the query string for a vod_streams/series request. When `limit` is set
// the server returns a paged/sorted/searchable slice instead of the whole
// catalog (Movies/Series browsing); without it, legacy full-list behavior.
function buildStreamParams(categoryId, options = {}) {
    const params = [];
    if (categoryId) params.push(`category_id=${encodeURIComponent(categoryId)}`);
    if (options.includeHidden) params.push('includeHidden=true');
    if (options.limit != null) params.push(`limit=${encodeURIComponent(options.limit)}`);
    if (options.offset != null) params.push(`offset=${encodeURIComponent(options.offset)}`);
    if (options.search) params.push(`search=${encodeURIComponent(options.search)}`);
    if (options.sort) params.push(`sort=${encodeURIComponent(options.sort)}`);
    if (options.genre) params.push(`genre=${encodeURIComponent(options.genre)}`);
    if (options.names) params.push('names=1'); // compact {i,n} index for fuzzy search
    if (options.ids) params.push(`ids=${encodeURIComponent(options.ids)}`); // full items for these ids
    return params.length ? `?${params.join('&')}` : '';
}

const API = {
    /**
     * Make API request
     *
     * A handful of the app's own boot-time calls (sync status, dashboard
     * favorites, initial EPG fetch) would occasionally throw a network-level
     * "Failed to fetch" right at page load - reproduced repeatedly and traced to
     * fetch() itself never reaching the server (not an HTTP error response), a
     * timing quirk rather than anything wrong server-side. One silent retry
     * covers it without masking real failures: HTTP error responses (4xx/5xx)
     * are never retried here, only the case where fetch() couldn't complete
     * the request at all.
     */
    async request(method, endpoint, data = null, _isRetry = false) {
        const options = {
            method,
            headers: {
                'Content-Type': 'application/json'
            }
        };

        // Add authentication token if available
        const token = localStorage.getItem('authToken');
        if (token) {
            options.headers['Authorization'] = `Bearer ${token}`;
        }

        if (data) {
            options.body = JSON.stringify(data);
        }

        let response;
        try {
            response = await fetch(`/api${endpoint}`, options);
        } catch (networkErr) {
            if (!_isRetry) {
                await new Promise(resolve => setTimeout(resolve, 400));
                return API.request(method, endpoint, data, true);
            }
            throw networkErr;
        }

        let result;
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            result = await response.json();
        } else {
            const text = await response.text();
            result = { error: text || 'API request failed' };
        }

        if (!response.ok) {
            // If unauthorized, redirect to login
            if (response.status === 401) {
                localStorage.removeItem('authToken');
                window.location.href = '/login.html';
                return;
            }
            throw new Error(result.error || `Server responded with ${response.status}`);
        }

        return result;
    },

    // Sources
    sources: {
        getAll: () => API.request('GET', '/sources'),
        getByType: (type) => API.request('GET', `/sources/type/${type}`),
        getById: (id) => API.request('GET', `/sources/${id}`),
        create: (data) => API.request('POST', '/sources', data),
        update: (id, data) => API.request('PUT', `/sources/${id}`, data),
        delete: (id) => API.request('DELETE', `/sources/${id}`),
        toggle: (id) => API.request('POST', `/sources/${id}/toggle`),
        test: (id) => API.request('POST', `/sources/${id}/test`),
        sync: (id) => API.request('POST', `/sources/${id}/sync`), // Manual sync
        getStatus: () => API.request('GET', '/sources/status'), // Get all statuses
        estimate: (id) => API.request('GET', `/sources/${id}/estimate`), // Estimate M3U size
        estimateByUrl: (url, type) => API.request('POST', '/sources/estimate', { url, type }), // Estimate by URL (before creation)
    },

    // Channels (hidden items)
    channels: {
        // contentType ('live'|'movie'|'series') scopes the response to just
        // that type - always pass it when the caller only cares about one
        // type (which is every current caller). See server route comment:
        // this can be a 200k+ row / multi-MB response unscoped.
        getHidden: (sourceId = null, contentType = null) => {
            const params = new URLSearchParams();
            if (sourceId) params.set('sourceId', sourceId);
            if (contentType) params.set('contentType', contentType);
            const qs = params.toString();
            return API.request('GET', `/channels/hidden${qs ? `?${qs}` : ''}`);
        },
        hide: (sourceId, itemType, itemId) => API.request('POST', '/channels/hide', { sourceId, itemType, itemId }),
        show: (sourceId, itemType, itemId) => API.request('POST', '/channels/show', { sourceId, itemType, itemId }),
        isHidden: (sourceId, itemType, itemId) => API.request('GET', `/channels/hidden/check?sourceId=${sourceId}&itemType=${itemType}&itemId=${itemId}`),
        bulkHide: (items) => API.request('POST', '/channels/hide/bulk', { items }),
        bulkShow: (items) => API.request('POST', '/channels/show/bulk', { items }),
        // Fast bulk operations - single SQL statement
        showAll: (sourceId, contentType) => API.request('POST', '/channels/show/all', { sourceId, contentType }),
        hideAll: (sourceId, contentType) => API.request('POST', '/channels/hide/all', { sourceId, contentType })
    },

    // Favorites
    favorites: {
        getAll: (sourceId = null, itemType = null) => {
            let url = '/favorites';
            const params = [];
            if (sourceId) params.push(`sourceId=${sourceId}`);
            if (itemType) params.push(`itemType=${itemType}`);
            if (params.length) url += '?' + params.join('&');
            return API.request('GET', url);
        },
        add: (sourceId, itemId, itemType = 'channel') =>
            API.request('POST', '/favorites', { sourceId, itemId, itemType }),
        remove: (sourceId, itemId, itemType = 'channel') =>
            API.request('DELETE', '/favorites', { sourceId, itemId, itemType }),
        check: (sourceId, itemId, itemType = 'channel') =>
            API.request('GET', `/favorites/check?sourceId=${sourceId}&itemId=${itemId}&itemType=${itemType}`),
        // Favorite channels already resolved to name/logo/streamId server-side,
        // so the Home row can render without loading the whole channel catalog.
        getResolvedChannels: () => API.request('GET', '/favorites/channels'),
        // Favorite movies/series resolved to full grid items, so the Movies/
        // Series "favorites only" filter works without holding the whole catalog.
        getResolvedItems: (type) => API.request('GET', `/favorites/items?type=${encodeURIComponent(type)}`)
    },

    // Watchlist ("Watch Later" - movies/series only)
    watchlist: {
        getAll: (sourceId = null, itemType = null) => {
            let url = '/watchlist';
            const params = [];
            if (sourceId) params.push(`sourceId=${sourceId}`);
            if (itemType) params.push(`itemType=${itemType}`);
            if (params.length) url += '?' + params.join('&');
            return API.request('GET', url);
        },
        add: (sourceId, itemId, itemType) =>
            API.request('POST', '/watchlist', { sourceId, itemId, itemType }),
        remove: (sourceId, itemId, itemType) =>
            API.request('DELETE', '/watchlist', { sourceId, itemId, itemType }),
        check: (sourceId, itemId, itemType) =>
            API.request('GET', `/watchlist/check?sourceId=${sourceId}&itemId=${itemId}&itemType=${itemType}`),
        getResolvedItems: (type) => API.request('GET', `/watchlist/items?type=${encodeURIComponent(type)}`)
    },

    // Watched (explicit "mark as watched", separate from watch_history progress)
    watched: {
        getAll: (itemType = null) =>
            API.request('GET', `/watched${itemType ? `?itemType=${itemType}` : ''}`),
        add: (sourceId, itemId, itemType, seasonCount = null, episodeCount = null) =>
            API.request('POST', '/watched', { sourceId, itemId, itemType, seasonCount, episodeCount }),
        remove: (sourceId, itemId, itemType) =>
            API.request('DELETE', '/watched', { sourceId, itemId, itemType }),
        check: (sourceId, itemId, itemType) =>
            API.request('GET', `/watched/check?sourceId=${sourceId}&itemId=${itemId}&itemType=${itemType}`)
    },

    // TMDB/trailer metadata
    metadata: {
        trailerSearch: (title, year, type) =>
            API.request('GET', `/metadata/trailer-search?title=${encodeURIComponent(title)}&year=${encodeURIComponent(year || '')}&type=${type}`),
        trailerSelect: (sourceId, itemId, type, videoId) =>
            API.request('POST', '/metadata/trailer-select', { sourceId, itemId, type, videoId }),
        getCredits: (sourceId, itemId, url) =>
            API.request('GET', `/metadata/credits?sourceId=${encodeURIComponent(sourceId)}&itemId=${encodeURIComponent(itemId)}&url=${encodeURIComponent(url)}`)
    },

    // Proxy
    proxy: {
        // Xtream
        xtream: {
            auth: (sourceId) => API.request('GET', `/proxy/xtream/${sourceId}/auth`),
            liveCategories: (sourceId, options = {}) => {
                const params = options.includeHidden ? '?includeHidden=true' : '';
                return API.request('GET', `/proxy/xtream/${sourceId}/live_categories${params}`);
            },
            liveStreams: (sourceId, categoryId = null, options = {}) => {
                const params = [];
                if (categoryId) params.push(`category_id=${categoryId}`);
                if (options.includeHidden) params.push('includeHidden=true');
                const query = params.length ? `?${params.join('&')}` : '';
                return API.request('GET', `/proxy/xtream/${sourceId}/live_streams${query}`);
            },
            vodCategories: (sourceId, options = {}) => {
                const params = options.includeHidden ? '?includeHidden=true' : '';
                return API.request('GET', `/proxy/xtream/${sourceId}/vod_categories${params}`);
            },
            vodStreams: (sourceId, categoryId = null, options = {}) => {
                const params = buildStreamParams(categoryId, options);
                return API.request('GET', `/proxy/xtream/${sourceId}/vod_streams${params}`);
            },
            genres: (sourceId, type = 'movie') =>
                API.request('GET', `/proxy/xtream/${sourceId}/genres?type=${type}`),
            seriesCategories: (sourceId, options = {}) => {
                const params = options.includeHidden ? '?includeHidden=true' : '';
                return API.request('GET', `/proxy/xtream/${sourceId}/series_categories${params}`);
            },
            series: (sourceId, categoryId = null, options = {}) => {
                const params = buildStreamParams(categoryId, options);
                return API.request('GET', `/proxy/xtream/${sourceId}/series${params}`);
            },
            seriesInfo: (sourceId, seriesId) =>
                API.request('GET', `/proxy/xtream/${sourceId}/series_info?series_id=${seriesId}`),
            shortEpg: (sourceId, streamId) => API.request('GET', `/proxy/xtream/${sourceId}/short_epg?stream_id=${streamId}`),
            getStreamUrl: (sourceId, streamId, type = 'live', container = 'm3u8') =>
                API.request('GET', `/proxy/xtream/${sourceId}/stream/${streamId}/${type}?container=${container}`),
            getTimeshiftUrl: (sourceId, streamId, startEpochMs, durationMinutes) =>
                API.request('GET', `/proxy/xtream/${sourceId}/timeshift/${streamId}?start=${startEpochMs}&duration=${durationMinutes}`)
        },

        // EPG
        epg: {
            get: (sourceId) => API.request('GET', `/proxy/epg/${sourceId}`),
            // Small channel-identity list only (no programmes) - lets a caller
            // resolve which channels it actually needs full schedule data for
            // before fetching the much heavier /programmes payload.
            getChannels: (sourceId) => API.request('GET', `/proxy/epg/${sourceId}/channels`),
            // Programmes scoped to specific channel ids - the fast path for a
            // guide's first paint. See EpgGuide.js's phased loadEpg().
            getProgrammes: (sourceId, channelIds) => API.request('POST', `/proxy/epg/${sourceId}/programmes`, { channelIds }),
            getMeta: (sourceId) => API.request('GET', `/proxy/epg/${sourceId}/meta`),
            // Descriptions are omitted from the bulk EPG payload to keep it small;
            // the programme detail modal fetches the one it needs on demand.
            getDescription: (sourceId, channelId, startMs) =>
                API.request('GET', `/proxy/epg/${sourceId}/description?channelId=${encodeURIComponent(channelId)}&start=${startMs}`)
        },

        // Cache management
        cache: {
            clear: (sourceId) => API.request('DELETE', `/proxy/cache/${sourceId}`)
        }
    },

    // Settings
    settings: {
        get: () => API.request('GET', '/settings'),
        update: (data) => API.request('PUT', '/settings', data),
        reset: () => API.request('DELETE', '/settings'),
        getDefaults: () => API.request('GET', '/settings/defaults'),
        getSyncStatus: () => API.request('GET', '/settings/sync-status')
    },

    // Users (admin only)
    users: {
        getAll: () => API.request('GET', '/auth/users'),
        create: (data) => API.request('POST', '/auth/users', data),
        update: (id, data) => API.request('PUT', `/auth/users/${id}`, data),
        delete: (id) => API.request('DELETE', `/auth/users/${id}`)
    },

    // Playback telemetry - reports events (stalls, quality switches, errors)
    // that previously only ever reached the browser's own devtools console,
    // into the same structured server-side log used by transcode/probe/proxy
    // (see server/routes/telemetry.js). Deliberately fire-and-forget - a
    // failed telemetry report must never affect playback itself, so this
    // swallows its own errors rather than letting a caller `await` into a
    // rejection.
    telemetry: {
        report: (event, meta = {}) => {
            fetch('/api/telemetry', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ event, ...meta })
            }).catch(() => {});
        }
    }
};

// Make API available globally
window.API = API;
